/**
 * Upstox V3 Market Data Feed Protobuf Decoder
 * 
 * Decodes binary protobuf messages from Upstox V3 WebSocket into JSON.
 * Based on the official Upstox MarketDataFeedV3.proto schema.
 */
import protobuf from 'protobufjs';

// Define the proto schema inline (based on Upstox V3 MarketDataFeed proto)
const protoDefinition = `
syntax = "proto3";

message FeedResponse {
  enum Type {
    initial_feed = 0;
    market_info = 1;
    live_feed = 2;
  }
  Type type = 1;
  map<string, Feed> feeds = 2;
  string currentTs = 3;
  MarketInfo marketInfo = 4;
}

message MarketInfo {
  map<string, string> segmentStatus = 1;
}

message Feed {
  LTPC ltpc = 1;
  FullFeed ff = 2;
  OptionChain oc = 3;
}

message LTPC {
  double ltp = 1;
  int64 ltt = 2;
  int64 ltq = 3;
  double cp = 4;
}

message FullFeed {
  MarketFullFeed marketFF = 1;
  IndexFullFeed indexFF = 2;
}

message MarketFullFeed {
  LTPC ltpc = 1;
  MarketLevel marketLevel = 2;
  OptionGreeks optionGreeks = 3;
  MarketOHLC marketOHLC = 4;
  ExtendedFeedDetails eFeedDetails = 5;
}

message IndexFullFeed {
  LTPC ltpc = 1;
  MarketOHLC marketOHLC = 2;
  string lastClose = 3;
  string yearHigh = 4;
  string yearLow = 5;
}

message MarketLevel {
  repeated Quote bidAskQuote = 1;
}

message Quote {
  int32 bq = 1;
  double bp = 2;
  int32 bno = 3;
  int32 aq = 4;
  double ap = 5;
  int32 ano = 6;
}

message OptionGreeks {
  double op = 1;
  double up = 2;
  double iv = 3;
  double delta = 4;
  double theta = 5;
  double gamma = 6;
  double vega = 7;
  double rho = 8;
}

message MarketOHLC {
  repeated OHLC ohlc = 1;
}

message OHLC {
  string interval = 1;
  double open = 2;
  double high = 3;
  double low = 4;
  double close = 5;
  int64 volume = 6;
  int64 ts = 7;
}

message ExtendedFeedDetails {
  double atp = 1;
  double cp = 2;
  int64 vtt = 3;
  int64 oi = 4;
  int64 tbq = 5;
  int64 tsq = 6;
  double lc = 7;
  double uc = 8;
  double fp = 9;
  int32 fv = 10;
  int64 dhoi = 11;
  int64 dloi = 12;
  int64 poi = 13;
}

message OptionChain {
  LTPC ltpc = 1;
  Quote bidAskQuote = 2;
  OptionGreeks optionGreeks = 3;
  ExtendedFeedDetails eFeedDetails = 4;
}
`;

let FeedResponse = null;
let protoReady = false;

// Parse the proto definition
try {
    const root = protobuf.parse(protoDefinition, { keepCase: true }).root;
    FeedResponse = root.lookupType('FeedResponse');
    protoReady = true;
} catch (e) {
    console.error('[Upstox Proto] Failed to load protobuf schema:', e);
}

/**
 * Decode a protobuf binary message from Upstox V3 WebSocket.
 * @param {Uint8Array} buffer - The binary message data
 * @returns {Object|null} Decoded message as JSON object, or null if decode fails
 */
export function decodeUpstoxFeed(buffer) {
    if (!protoReady || !FeedResponse) {
        return null;
    }

    try {
        const message = FeedResponse.decode(buffer);
        const obj = FeedResponse.toObject(message, {
            longs: Number,
            enums: String,
            defaults: false
        });
        return obj;
    } catch (e) {
        console.warn('[Upstox Proto] Decode error:', e.message);
        return null;
    }
}

export function isProtoReady() {
    return protoReady;
}
